import { CreateProductRequest } from '@wavingroup/aqora-v2-api/wavin/aqora/v2/product_mutation_pb';
import { CreateProductType } from '@wavingroup/aqora-v2-api/wavin/aqora/v2/system_pb';
import { TFunction } from 'i18next';
import {
  CloudRainIcon,
  LevelIcon,
  TapIcon,
} from '~/shared/components/Icons/Icons';

type SystemProduct = CreateProductType.CONTROLLER;

type DrainProduct =
  | CreateProductType.POLDER_VALVE
  | CreateProductType.VALVE
  | CreateProductType.PUMP;

type ReservoirProducts = Exclude<
  CreateProductType,
  SystemProduct | DrainProduct
>;

type ProductParams =
  | {
      type: SystemProduct;
      systemName: string;
    }
  | {
      type: DrainProduct;
      reservoirName: string;
      drainName: string;
    }
  | {
      type: ReservoirProducts;
      reservoirName: string;
    };

export class CreateProductModel {
  static getCreateProductDetails(params: ProductParams, t: TFunction) {
    let request: CreateProductRequest;

    switch (params.type) {
      case CreateProductType.CONTROLLER:
        request = new CreateProductRequest({
          type: params.type,
          systemName: params.systemName,
        });
        break;
      case CreateProductType.POLDER_VALVE:
      case CreateProductType.VALVE:
      case CreateProductType.PUMP:
        request = new CreateProductRequest({
          type: params.type,
          reservoirName: params.reservoirName,
          drainName: params.drainName,
        });
        break;
      default:
        request = new CreateProductRequest({
          type: params.type,
          reservoirName: params.reservoirName,
        });
        break;
    }

    const label = CreateProductModel.getLabel(params.type, t);
    const icon = CreateProductModel.getIcon(params.type);

    return { request, label, icon };
  }

  private static getIcon(type: CreateProductType) {
    switch (type) {
      case CreateProductType.TAP:
        return <TapIcon />;
      case CreateProductType.STAAL_RADAR_LEVEL:
        return <LevelIcon />;
      case CreateProductType.LEVEL_SENSOR:
        return <LevelIcon />;
      case CreateProductType.RAIN_METER:
        return <CloudRainIcon />;
      default:
        return undefined;
    }
  }

  static getErrorMessage(type: CreateProductType, t: TFunction) {
    switch (type) {
      case CreateProductType.TAP:
        return t('systemDesign.product.tap.create.error');
      case CreateProductType.STAAL_RADAR_LEVEL:
        return t('systemDesign.product.waterLevelSensor.create.error');
      case CreateProductType.LEVEL_SENSOR:
        return t('systemDesign.product.levelSensor.create.error');
      case CreateProductType.RAIN_METER:
        return t('systemDesign.product.rainSensor.create.error');
      case CreateProductType.CONTROLLER:
        return t('systemDesign.product.controller.create.error');
      case CreateProductType.PUMP:
        return t('systemDesign.product.pump.create.error');
      case CreateProductType.POLDER_VALVE:
        return t('systemDesign.product.polderValve.create.error');
      case CreateProductType.VALVE:
        return t('systemDesign.product.valve.create.error');

      default:
        return '';
    }
  }

  private static getLabel(type: CreateProductType, t: TFunction) {
    switch (type) {
      case CreateProductType.TAP:
        return t('systemDesign.product.tap.create.button');
      case CreateProductType.STAAL_RADAR_LEVEL:
        return t('systemDesign.product.waterLevelSensor.create.button');
      case CreateProductType.LEVEL_SENSOR:
        return t('systemDesign.product.levelSensor.create.button');
      case CreateProductType.RAIN_METER:
        return t('systemDesign.product.rainSensor.create.button');
      case CreateProductType.CONTROLLER:
        return t('systemDesign.product.controller.create.button');
      case CreateProductType.PUMP:
        return t('systemDesign.product.pump.create.button');
      case CreateProductType.POLDER_VALVE:
        return t('systemDesign.product.polderValve.create.button');
      case CreateProductType.VALVE:
        return t('systemDesign.product.valve.create.button');

      default:
        return '';
    }
  }
}
